import { ChevronDown, GitBranch, LoaderCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../web/src/api";
import type { ProjectGitStatus } from "../../../web/src/api/types";

export function ProjectBranchSwitcher({
  projectId,
  onSwitched,
}: {
  projectId?: string;
  onSwitched?(): void;
}) {
  const [status, setStatus] = useState<ProjectGitStatus>();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string>();
  const rootRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    if (!projectId) {
      setStatus(undefined);
      return;
    }
    setLoading(true);
    try {
      setStatus(await api.getProjectGitStatus(projectId));
      setError(undefined);
    } catch (cause) {
      setStatus(undefined);
      setError(cause instanceof Error ? cause.message : "无法读取项目分支。");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const branches = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return status?.branches.filter(
      (branch) => !normalizedQuery || branch.toLocaleLowerCase().includes(normalizedQuery),
    ) ?? [];
  }, [query, status?.branches]);

  const switchBranch = useCallback(
    async (branch: string) => {
      if (
        !projectId ||
        !status?.headOid ||
        branch === status.currentBranch ||
        switching
      ) {
        return;
      }
      if (!status.canSwitch) {
        setError(status.reason ?? "当前项目不能切换分支。");
        return;
      }
      setSwitching(true);
      try {
        const next = await api.switchProjectGitBranch({
          projectId,
          branch,
          expectedHeadOid: status.headOid,
        });
        setStatus(next);
        setOpen(false);
        setQuery("");
        setError(undefined);
        onSwitched?.();
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "切换项目分支失败。");
        await refresh();
      } finally {
        setSwitching(false);
      }
    },
    [onSwitched, projectId, refresh, status, switching],
  );

  if (!status?.available || !status.currentBranch) return null;

  const dirty = status.dirty.staged || status.dirty.unstaged || status.dirty.untracked;
  return (
    <div className="project-branch-switcher" ref={rootRef}>
      <button
        type="button"
        className="project-branch-trigger"
        data-testid="project-branch-trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={loading}
        onClick={() => {
          setOpen((current) => !current);
          setQuery("");
        }}
      >
        {loading || switching ? (
          <LoaderCircle className="project-branch-spinner" size={13} aria-hidden="true" />
        ) : (
          <GitBranch size={13} aria-hidden="true" />
        )}
        <span>{status.currentBranch}</span>
        <ChevronDown size={13} aria-hidden="true" />
      </button>
      {open && (
        <section className="project-branch-popover" aria-label="切换项目分支">
          <label className="project-branch-search">
            <span className="visually-hidden">筛选分支</span>
            <input
              autoFocus
              type="search"
              value={query}
              placeholder="筛选分支"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          {dirty && (
            <p className="project-branch-warning" role="status">
              当前项目有未提交改动。请先提交或手动暂存后再切换分支。
            </p>
          )}
          {error && <p className="project-branch-error" role="alert">{error}</p>}
          <div className="project-branch-options" role="listbox" aria-label="可用分支">
            {branches.map((branch) => (
              <button
                type="button"
                key={branch}
                role="option"
                aria-selected={branch === status.currentBranch}
                disabled={switching || branch === status.currentBranch || !status.canSwitch}
                onClick={() => void switchBranch(branch)}
              >
                <GitBranch size={14} aria-hidden="true" />
                <span>{branch}</span>
                {branch === status.currentBranch && <small>当前</small>}
              </button>
            ))}
            {!branches.length && <p>没有匹配的本地分支。</p>}
          </div>
        </section>
      )}
    </div>
  );
}
